import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { trackCta } from "@/lib/analytics";
import {
  CATEGORY_SLUGS,
  CATEGORY_LABEL_KEYS,
  fetchAllArticles,
} from "@/data/journalArticles";
import {
  TAG_GROUPS,
  normalizeTag,
  articleMatchesFilter,
  articleMatchesQuery,
} from "@/data/journalTags";
import ArticleCard from "@/components/journal/ArticleCard";

const HERO_LANDSCAPE = "https://customer-assets-jt897jd0.emergentagent.net/job_virtue-guide-demo/artifacts/rxs7vhl7_IMG_7284.jpeg";
const HERO_PORTRAIT = "https://customer-assets-jt897jd0.emergentagent.net/job_virtue-guide-demo/artifacts/8rjuekm9_IMG_7285.jpeg";

/**
 * /stoic-journal — Stoic Knowledge Library.
 *
 *   • Hero (eyebrow / title / lede / intro)
 *   • Featured article hero card (if any article is marked featured)
 *   • Live search across title / excerpt / content / author / tags
 *   • Smart filter chips: legacy categories + tag taxonomy (philosophers,
 *     themes, virtues, practices) — chips appear automatically once an
 *     article carries the tag; no duplicates with the main row
 *   • Grid of remaining articles + empty state
 */
function HeroCopy({ t }) {
  const scrollToLibrary = () =>
    document.getElementById("journal-search-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <>
      <h1
        data-testid="journal-hero-title"
        className="font-serif uppercase text-bone leading-[1.08] tracking-[0.05em] text-4xl sm:text-5xl lg:text-6xl"
      >
        <span className="block">The Stoic</span>
        <span className="block">Journal</span>
      </h1>
      <div className="gold-rule w-16 my-5" aria-hidden />
      <p
        data-testid="journal-hero-tagline"
        className="font-serif text-bone/95 text-lg sm:text-xl lg:text-2xl leading-snug tracking-tight"
      >
        {t("journal.hero.tagline")}
      </p>
      <p
        data-testid="journal-hero-lede"
        className="mt-5 text-bone/85 font-reading text-base md:text-lg leading-[1.8] max-w-md"
      >
        {t("journal.hero.lede")}
      </p>
      <button
        type="button"
        data-testid="journal-hero-cta"
        onClick={scrollToLibrary}
        className="mt-8 inline-flex items-center gap-3 px-7 py-3.5 bg-gold hover:bg-gold-hover text-ink text-[11px] tracking-[0.26em] uppercase font-semibold transition-colors"
      >
        {t("journal.hero.cta")} <span aria-hidden>→</span>
      </button>
    </>
  );
}

export default function Journal() {
  const { t, lang } = useT();
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [allArticles, setAllArticles] = useState([]);

  // Merge file-based + DB-published articles on mount
  useEffect(() => {
    let cancelled = false;
    fetchAllArticles().then((list) => {
      if (!cancelled) setAllArticles(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Taxonomy chips: only tags that exist on ≥1 article and are not already
  // covered by the main category row (no duplicates).
  const extraGroups = useMemo(() => {
    const present = new Set();
    allArticles.forEach((a) => (a.tags || []).forEach((tag) => present.add(normalizeTag(tag))));
    const covered = new Set(CATEGORY_SLUGS);
    return TAG_GROUPS.map((g) => ({
      ...g,
      tags: g.tags.filter((tag) => {
        const s = normalizeTag(tag);
        return present.has(s) && !covered.has(s);
      }),
    })).filter((g) => g.tags.length > 0);
  }, [allArticles]);

  const matches = (a) => articleMatchesFilter(a, active) && articleMatchesQuery(a, query);

  const featured = allArticles.find((a) => a.featured);
  const visible = useMemo(
    () => allArticles.filter((a) => !a.featured && matches(a)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, query, allArticles]
  );
  const filteredFeatured = featured && matches(featured) ? featured : null;

  const title = t("journal.title");
  const description = t("journal.intro");

  useSEO({
    title: `${title} | StoicOs`,
    description,
    canonical: langCanonical("/stoic-journal", lang),
    alternates: langAlternates("/stoic-journal"),
    og: { title: `${title} | StoicOs`, description },
    jsonLd: [
      webPage({ name: title, description, url: "/stoic-journal" }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: title, url: "/stoic-journal" },
      ]),
    ],
  });

  const chipClass = (isActive) =>
    `px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
      isActive
        ? "border-gold text-gold bg-gold/5"
        : "border-white/10 text-bone/75 hover:text-bone hover:border-white/25"
    }`;

  const selectFilter = (slug) => {
    setActive(slug);
    trackCta("journal_filter", { category: slug });
  };

  return (
    <main data-testid="journal-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* breadcrumb */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-12"
          data-testid="journal-back"
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs · Virtue Guide
        </Link>

        {/* Hero — responsive image (landscape ≥768px, portrait below), all
            text as HTML overlay (desktop) or below the image (mobile) */}
        <section data-testid="journal-hero" className="mt-2">
          <div className="relative overflow-hidden border border-white/10">
            <picture>
              <source media="(min-width: 768px)" srcSet={HERO_LANDSCAPE} />
              <img
                src={HERO_PORTRAIT}
                alt="Stoic philosopher overlooking an ancient city at sunset"
                className="w-full object-cover aspect-[4/5] object-[center_28%] md:aspect-auto md:h-[500px] lg:h-[560px] md:object-[center_38%]"
                data-testid="journal-hero-image"
              />
            </picture>
            <div
              className="hidden md:block absolute inset-y-0 left-0 w-[70%] bg-gradient-to-r from-ink/90 via-ink/45 to-transparent pointer-events-none"
              aria-hidden
            />
            <div className="hidden md:flex absolute inset-0 items-center">
              <div className="px-10 lg:px-16 max-w-xl">
                <HeroCopy t={t} />
              </div>
            </div>
          </div>
          <div className="md:hidden mt-8">
            <HeroCopy t={t} />
          </div>
        </section>

        {/* Featured article */}
        {filteredFeatured && (
          <section className="mt-16 md:mt-20" data-testid="journal-featured-section">
            <ArticleCard article={filteredFeatured} variant="featured" />
          </section>
        )}

        {/* Search */}
        <section className="mt-20 md:mt-24 scroll-mt-28" id="journal-search-section" data-testid="journal-search-section">
          <div className="relative max-w-xl">
            <Search
              size={15}
              strokeWidth={1.5}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-fog/70 pointer-events-none"
              aria-hidden
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("journal.search.placeholder")}
              aria-label={t("journal.search.placeholder")}
              data-testid="journal-search"
              className="w-full bg-ink-elevated/40 border border-bone/60 focus:border-gold outline-none pl-11 pr-4 py-3.5 text-bone font-reading text-base placeholder:text-fog/60 transition-colors"
            />
          </div>
          {query.trim() && (
            <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-fog/60" data-testid="journal-search-count">
              {(visible.length + (filteredFeatured ? 1 : 0))} {t("journal.search.results")}
            </p>
          )}
        </section>

        {/* Category tabs + smart tag chips */}
        <section className="mt-10" data-testid="journal-filter-bar">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">
            {t("journal.browseLabel")}
          </p>
          <div
            className="flex flex-wrap gap-2.5"
            role="tablist"
            aria-label={t("journal.browseLabel")}
          >
            {CATEGORY_SLUGS.map((slug) => (
              <button
                key={slug}
                type="button"
                role="tab"
                aria-selected={active === slug}
                data-testid={`journal-tab-${slug}`}
                onClick={() => selectFilter(slug)}
                className={chipClass(active === slug)}
              >
                {t(CATEGORY_LABEL_KEYS[slug])}
              </button>
            ))}
          </div>

          {extraGroups.map((group) => (
            <div key={group.key} className="mt-6" data-testid={`journal-tag-group-${group.key}`}>
              <p className="text-[10px] tracking-[0.28em] uppercase text-fog/60 mb-3">
                {t(group.labelKey)}
              </p>
              <div className="flex flex-wrap gap-2.5">
                {group.tags.map((tag) => {
                  const slug = normalizeTag(tag);
                  return (
                    <button
                      key={slug}
                      type="button"
                      role="tab"
                      aria-selected={active === slug}
                      data-testid={`journal-tag-${slug}`}
                      onClick={() => selectFilter(slug)}
                      className={chipClass(active === slug)}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* Grid of articles / empty state */}
        <section className="mt-12" data-testid="journal-grid">
          {visible.length === 0 && !filteredFeatured ? (
            <div
              data-testid="journal-empty"
              className="p-10 md:p-14 border border-white/8 bg-ink-elevated/40 text-center"
            >
              <p className="font-serif text-2xl md:text-3xl text-bone tracking-tight">
                {t("journal.empty.title")}
              </p>
              <p className="mt-4 max-w-xl mx-auto text-bone/75 font-reading text-base md:text-lg leading-[1.85]">
                {t("journal.empty.body")}
              </p>
            </div>
          ) : visible.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {visible.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
